import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { searchAllPracticesAtom } from '../modules/patientList/atoms';
import { useDebounce } from '../utils/hooks/useDebounce';

import { makeAPIRequestReactQuery } from './api';

export interface PatientSearchResult {
  name: string;
  imagingPatientVideaId: string;
  pmsPatientVideaId?: string | null;
  dateOfBirth?: string | null;
  lastAppointment?: string | null;
}

export interface PatientSearchResponse {
  patients: PatientSearchResult[];
}

export const usePatientSearch = (
  patientName: string | null,
  options?: UseQueryOptions<
    PatientSearchResponse,
    unknown,
    PatientSearchResponse,
    string[]
  >,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector((state: any) => state.login.user);
  const isOrgSearchEnabled = useAtomValue(searchAllPracticesAtom);

  const { debouncedValue: debouncedPatientName } = useDebounce(
    patientName || '',
    300,
  );

  return useQuery(
    [currentPractice, 'patients', 'search', debouncedPatientName],
    () =>
      makeAPIRequestReactQuery<PatientSearchResponse>(
        `patient/search`,
        undefined,
        'get',
        {
          params: {
            name: debouncedPatientName,
            orgSearch: isOrgSearchEnabled,
          },
          headers: {
            Practice: currentPractice,
            Org: currentOrg,
          },
        },
      ),
    {
      enabled:
        !!currentPractice &&
        !!currentOrg &&
        !!debouncedPatientName &&
        debouncedPatientName.length >= 2,
      ...options,
    },
  );
};
